import { Observable } from "rxjs";
import { HttpAgent, EventType } from "@ag-ui/client";
import type { RunAgentInput, StateSnapshotEvent } from "@ag-ui/client";
import type { JobProgress, OutputRef } from "../model/block.types";

/**
 * Attaches to an existing run's SSE stream using the CopilotKit protocol format:
 * { method, params: { agentId }, body: RunAgentInput }
 * Same envelope as a regular agent/run call — backend differentiates by runId presence.
 */
export class ConnectRunAgent extends HttpAgent {
  protected override requestInit(input: RunAgentInput): RequestInit {
    return {
      ...super.requestInit(input),
      body: JSON.stringify({
        method: "agent/run/stream",
        params: { agentId: this.agentId },
        body: input,
      }),
    };
  }
}

export interface RunStreamEvent {
  type: "state";
  state: Record<string, unknown>;
  jobProgress: JobProgress | null;
  outputRefs: OutputRef[];
}

export interface RunStreamCustom {
  type: "custom";
  name: string;
  value: unknown;
}

export interface RunStreamTerminal {
  type: "finished" | "run_error";
}

export interface RunStreamFailed {
  type: "failed";
  error: unknown;
}

export type RunStreamItem = RunStreamEvent | RunStreamCustom | RunStreamTerminal | RunStreamFailed;

/**
 * Wraps ConnectRunAgent in an RxJS Observable.
 * Emits `state` items on each STATE_SNAPSHOT, `custom` for backend CUSTOM events — all routed
 * through the same `dispatch_stream_event`/`adispatch_custom_event` channel, namespaced:
 * `pipeline_phase_status`/`pipeline_phase_step` (generic phase/step progress, carries its own
 * `phase`/`step` key) and `deep_analysis_tool_call`/`deep_analysis_message_chunk`/
 * `deep_analysis_todo_update` (deep_analysis sub-agent activity — its turns never reach the
 * standard AG-UI message channel, same limitation documented in WorkerReasoningStreamService
 * for the main chat) — `finished`/`run_error` on terminal ag-ui events, and `failed` if the
 * SSE connection itself errors (e.g. 400 from proxy). Calls `abortRun()` on unsubscription.
 */
export function connectRunStream(opts: {
  url: string;
  headers: Record<string, string>;
  agentId: string;
  threadId: string;
  runId: string;
}): Observable<RunStreamItem> {
  return new Observable((observer) => {
    const agent = new ConnectRunAgent({
      url: opts.url,
      headers: opts.headers,
      agentId: opts.agentId,
      threadId: opts.threadId,
    });

    // Backend emits one STATE_SNAPSHOT per changed top-level key (job_progress,
    // discover_ref, providers, ...), not a full merged state each time. @ag-ui/client's
    // HttpAgent treats every STATE_SNAPSHOT as a full replace (see defaultApplyEvents),
    // so relying on its internal `state` (via onStateChanged) silently drops every key
    // not present in the latest partial snapshot. Reading the raw event via onEvent and
    // shallow-merging ourselves keeps previously-seen keys intact, matching what the
    // polling GET /state endpoint returns (LangGraph's fully merged checkpoint values).
    let accumulatedState: Record<string, unknown> = {};

    agent.subscribe({
      onEvent: ({ event }) => {
        if (event.type === EventType.STATE_SNAPSHOT) {
          const { snapshot } = event as StateSnapshotEvent;
          accumulatedState = { ...accumulatedState, ...(snapshot as Record<string, unknown>) };
          observer.next({
            type: "state",
            state: accumulatedState,
            jobProgress: (accumulatedState.job_progress as JobProgress) ?? null,
            outputRefs: (accumulatedState.output_refs as OutputRef[]) ?? [],
          });
        } else if (event.type === EventType.RUN_FINISHED) {
          observer.next({ type: "finished" });
        } else if (event.type === EventType.RUN_ERROR) {
          observer.next({ type: "run_error" });
        } else if (event.type === EventType.CUSTOM) {
          const e = event as { name: string; value: unknown };
          observer.next({ type: "custom", name: e.name, value: e.value });
        }
      },
      onRunFailed: ({ error }) => {
        observer.next({ type: "failed", error });
      },
    });

    agent.runAgent({ runId: opts.runId }).catch((err) => {
      // runAgent promise rejects after onRunFailed fires — already handled above.
      if (!observer.closed) observer.next({ type: "failed", error: err });
    });

    return () => agent.abortRun();
  });
}
